<template lang="pug">
.escality-navbar
  Toolbar.navbar-toolbar
    template(#start)
      // Logo/Brand area (opcional para mobile)
      .navbar-brand.mobile-only
        img.brand-logo(src="@/assets/isotipo-escality.svg" alt="ESCALITY")

      // Page Info
      .page-info
        .page-header
          h1.page-title {{ pageTitle }}
          .breadcrumb-container(v-if="breadcrumbs.length > 0")
            span.breadcrumb-separator /
            span.breadcrumb-item(v-for="(item, index) in breadcrumbs" :key="index")
              router-link(v-if="item.to" :to="item.to" class="breadcrumb-link") {{ item.label }}
              span(v-else class="breadcrumb-current") {{ item.label }}
              span.breadcrumb-separator(v-if="index < breadcrumbs.length - 1") /

    template(#end)
      // Subscription Alert
      transition(name="slide-fade")
        Chip.subscription-chip(
          v-if="showLastweek"
          icon="pi pi-exclamation-triangle"
          label="Suscripción próxima a vencer"
        )

      // Notifications
      .notification-wrapper
        Button.nav-btn.notifications-btn(
          @click="handleNotifications"
          icon="pi pi-bell"
          rounded
          text
          :class="{ 'has-notifications': showCountNotifications > 0 }"
        )
        Badge(
          v-if="notificationBadge"
          :value="notificationBadge"
          severity="danger"
          class="notification-badge"
        )

      // Quick Actions
      Button.nav-btn(
        @click="toggleQuickActions"
        icon="pi pi-th-large"
        rounded
        text
        v-tooltip.bottom="'Acciones rápidas'"
      )

      // Billing
      Button.nav-btn.billing-btn(
        @click="$router.push({ name: 'Billing' })"
        icon="pi pi-receipt"
        rounded
        text
        :class="billingBtnClass"
        v-tooltip.bottom="billingTooltip"
      )

      // Descargas
      Button.nav-btn(
        @click="$router.push({ name: 'Downloads' })"
        icon="pi pi-download"
        rounded
        text
        v-tooltip.bottom="'Descargas'"
      )

      // Configuración
      Button.nav-btn(
        @click="$router.push({ name: 'Configuración' })"
        icon="pi pi-cog"
        rounded
        text
        v-tooltip.bottom="'Configuración'"
      )

      // Help
      Button.nav-btn(
        @click="toggleHelpPanel"
        icon="pi pi-question-circle"
        rounded
        text
        v-tooltip.bottom="'Centro de ayuda'"
      )

      // Separator
      Divider(layout="vertical")

      // User Menu
      .user-menu-wrapper
        Button.user-menu-btn(
          @click="toggleUserMenu"
          aria-label="Menu de usuario"
          :class="{ 'menu-open': userMenuVisible }"
        )
          Avatar.user-avatar(
            :label="userInitial"
            shape="square"
            size="normal"
          )
          .user-info
            span.user-name {{ authUser.name || 'Usuario' }}
            //- Si el rol no viene, no se muestra nada: "Sin rol" era estado
              interno del sistema, no información útil para el usuario.
            span.user-role(v-if="authUser.role?.name") {{ authUser.role.name }}
          i.pi.pi-chevron-down.menu-arrow

  // Quick Actions Panel
  OverlayPanel(
    ref="quickActionsPanel"
    :showCloseIcon="false"
    :dismissable="true"
    :style="{ width: '400px' }"
    appendTo="body"
  )
    .quick-actions-panel
      .panel-header
        i.pi.pi-bolt
        h5 Acciones Rápidas

      .actions-grid
        .action-card(@click="navigateAndClose({ name: 'Nueva venta' })")
          .action-icon.icon-sales
            i.pi.pi-shopping-cart
          span Nueva Venta

        .action-card(@click="navigateAndClose({ name: 'Nuevo producto' })")
          .action-icon.icon-products
            i.pi.pi-box
          span Producto

        .action-card(@click="navigateAndClose({ name: 'Clientes' })")
          .action-icon.icon-clients
            i.pi.pi-users
          span Clientes

        .action-card(@click="navigateAndClose({ name: 'AiReports' })")
          .action-icon.icon-reports
            i.pi.pi-chart-line
          span Reportes

        .action-card(@click="navigateAndClose({ name: 'Categorias de inventario' })")
          .action-icon.icon-categories
            i.pi.pi-tags
          span Categorías

        .action-card(@click="navigateAndClose({ name: 'Downloads' })")
          .action-icon.icon-downloads
            i.pi.pi-download
          span Descargas

  // User Menu Panel
  OverlayPanel(
    ref="userMenuPanel"
    :showCloseIcon="false"
    :dismissable="true"
    :style="{ width: '280px' }"
    appendTo="body"
  )
    .user-menu-panel
      .user-header
        Avatar.user-avatar-large(
          :label="userInitial"
          size="xlarge"
          shape="square"
        )
        .user-details
          h5 {{ authUser.name || 'Usuario' }}
          span.user-email {{ authUser.email || 'email@example.com' }}
          Chip.user-status(
            :label="authUser.role?.name || 'Usuario'"
            icon="pi pi-verified"
          )

      Divider

      Menu(:model="userMenuItems")

  // Help Panel (Sidebar)
  Sidebar(
    :visible="helpPanelVisible"
    @update:visible="helpPanelVisible = $event"
    position="right"
    :showCloseIcon="true"
    :modal="true"
    :dismissable="true"
  )
    template(#header)
      .sidebar-header
        i.pi.pi-question-circle
        span Centro de Ayuda

    .help-content
      Card.help-card
        template(#header)
          i.pi.pi-video.help-icon
        template(#title) Video Tutoriales
        template(#content)
          p Aprende a usar todas las funciones del sistema con nuestros tutoriales paso a paso.
          Button(
            label="Ver Tutoriales"
            icon="pi pi-youtube"
            @click="openTutorials"
            fluid
          )

      Card.help-card.mt-3
        template(#header)
          i.pi.pi-headset.help-icon
        template(#title) Soporte Técnico
        template(#content)
          p ¿Necesitas ayuda personalizada? Nuestro equipo está listo para asistirte.
          Button(
            outlined
            label="Contactar Soporte"
            icon="pi pi-envelope"
            @click="contactSupport"
            fluid
          )

      Card.help-card.mt-3
        template(#header)
          i.pi.pi-book.help-icon
        template(#title) Documentación
        template(#content)
          p Consulta nuestra documentación completa del sistema.
          Button(
            outlined
            label="Ver Documentación"
            icon="pi pi-external-link"
            @click="openDocumentation"
            fluid
          )
</template>

<script>
import { useMeStore } from '@/stores/me';
import { useNotificationsStore } from '@/stores/notifications';
import { useBillingStore } from '@/stores/billing';

export default {
  name: 'Navbar',
  data() {
    return {
      breadcrumbs: [],
      homeItem: { icon: 'pi pi-home', to: '/' },
      userMenuVisible: false,
      helpPanelVisible: false
    };
  },
  computed: {
    authUser() {
      const meStore = useMeStore()
      return meStore.getUser || {}
    },
    showCountNotifications() {
      const notificationsStore = useNotificationsStore()
      return notificationsStore.showCountNotifications || 0
    },
    notificationBadge() {
      const count = this.showCountNotifications
      if (count === 0) return null
      return count > 99 ? '99+' : String(count)
    },
    billingStatus() {
      const billingStore = useBillingStore()
      if (billingStore.hasOverdue) return 'overdue'
      if (billingStore.pendingInvoices.length > 0) return 'pending'
      return 'ok'
    },
    billingBtnClass() {
      return {
        'billing-overdue': this.billingStatus === 'overdue',
        'billing-pending': this.billingStatus === 'pending',
      }
    },
    billingTooltip() {
      if (this.billingStatus === 'overdue') return 'Factura vencida - Pagar ahora'
      if (this.billingStatus === 'pending') return 'Factura pendiente de pago'
      return 'Facturación'
    },
    showLastweek() {
      const meStore = useMeStore()
      return meStore.showLastweek
    },
    pageTitle() {
      return this.$route.meta?.title || this.formatRouteName(this.$route.name) || 'Dashboard';
    },
    userInitial() {
      const name = this.authUser.name || 'U'
      return name.charAt(0).toUpperCase()
    },
    /**
     * Menú del usuario.
     *
     * Tenía cuatro entradas para dos destinos: "Mi Perfil" y "Cambiar
     * Contraseña" abrían la misma pantalla, y "Configuración" y "Datos de mi
     * Empresa" iban las dos al acordeón de sucursales. Cada entrada lleva
     * ahora a un lugar distinto, y "Configuración" al índice de la sección.
     *
     * El bloque "Empresa" estaba detrás de `this.$can('config.empresa')`.
     * $can no existe en esta app —los permisos se leen del store, con otro
     * formato— así que la condición era siempre falsa y el ítem no se mostró
     * nunca. Se elimina en vez de dejarlo escondido detrás de un permiso que
     * tampoco existe: su destino ya está en el índice, como "Sucursales".
     */
    userMenuItems() {
      return [
        {
          label: 'Mi Cuenta',
          items: [
            {
              label: 'Cambiar contraseña',
              icon: 'pi pi-lock',
              command: () => this.$router.push({ name: 'Cambiar contraseña' })
            },
            {
              label: 'Configuración',
              icon: 'pi pi-cog',
              command: () => this.$router.push({ name: 'Configuración' })
            }
          ]
        },
        {
          separator: true
        },
        {
          label: 'Cerrar Sesión',
          icon: 'pi pi-sign-out',
          class: 'logout-item',
          command: () => this.handleLogout()
        }
      ];
    }
  },
  watch: {
    '$route'() {
      this.updateBreadcrumbs();
      // Close all panels on route change
      this.closeAllPanels();
    },
    pageTitle: {
      handler(newTitle) {
        if (newTitle) {
          document.title = `${newTitle} - ESCALITY`;
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.updateBreadcrumbs();
    this.getNotifications();
    useBillingStore().fetchBilling();
  },
  methods: {
    handleNotifications() {
      this.$router.push({ name: 'Notificaciones' });
    },
    toggleQuickActions(event) {
      this.$refs.quickActionsPanel.toggle(event);
    },
    toggleUserMenu(event) {
      this.userMenuVisible = !this.userMenuVisible;
      this.$refs.userMenuPanel.toggle(event);
    },
    toggleHelpPanel() {
      this.helpPanelVisible = !this.helpPanelVisible;
    },
    navigateAndClose(route) {
      this.$refs.quickActionsPanel.hide();
      this.$router.push(route);
    },
    closeAllPanels() {
      if (this.$refs.quickActionsPanel) {
        this.$refs.quickActionsPanel.hide();
      }
      if (this.$refs.userMenuPanel) {
        this.$refs.userMenuPanel.hide();
      }
      this.helpPanelVisible = false;
      this.userMenuVisible = false;
    },
    getNotifications() {
      const notificationsStore = useNotificationsStore()
      notificationsStore.getNotifications()
    },
    formatRouteName(name) {
      if (!name) return '';
      return name.replace(/-/g, ' ').replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    updateBreadcrumbs() {
      const breadcrumbs = [];
      const paths = this.$route.path.split('/').filter(p => p);

      // En rutas de un solo nivel el breadcrumb repetiría el título de la
      // página ("Clientes / Clientes"): sin jerarquía que mostrar, no aporta.
      if (paths.length < 2) {
        this.breadcrumbs = [];
        return;
      }

      let currentPath = '';
      paths.forEach((path, index) => {
        currentPath += '/' + path;
        breadcrumbs.push({
          label: this.formatRouteName(path),
          to: index === paths.length - 1 ? null : currentPath
        });
      });

      this.breadcrumbs = breadcrumbs;
    },
    handleLogout() {
      // Close panels first
      this.closeAllPanels();
      // Navigate to logout
      this.$router.push({ name: 'logout' });
    },
    openTutorials() {
      window.open('https://www.youtube.com/channel/UC7Si9L9dIqmX1zPn7lasVdA', '_blank');
      this.helpPanelVisible = false;
    },
    contactSupport() {
      this.$toast.add({
        severity: 'info',
        summary: 'Soporte',
        detail: 'Enviando solicitud de soporte...',
        life: 3000
      });
      this.helpPanelVisible = false;
      // Aquí puedes agregar la lógica real de contacto
    },
    openDocumentation() {
      this.$toast.add({
        severity: 'info',
        summary: 'Documentación',
        detail: 'Abriendo documentación del sistema...',
        life: 3000
      });
      this.helpPanelVisible = false;
      // Aquí puedes agregar el enlace a la documentación
    }
  }
};
</script>

<style scoped>
/* ESCALITY Navbar Design System 2025 - Usando Variables Centralizadas */
.escality-navbar {
  position: fixed;
  top: 0;
  right: 0;
  left: var(--escality-sidebar-width);
  z-index: 1030;
  background: var(--escality-surface);
  border-bottom: 1px solid var(--escality-gray-200);
  transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Sidebar collapsed state */
.sidebar-collapsed .escality-navbar {
  left: var(--escality-sidebar-width-collapsed);
}

/* Toolbar customization */
.navbar-toolbar {
  height: var(--escality-navbar-height);
  padding: 0 24px;
  background: transparent;
  border: none;
  border-radius: 0;
}

.navbar-toolbar :deep(.p-toolbar-separator) {
  margin: 0 12px;
  height: 32px;
  background: var(--escality-gray-200);
}

/* Mobile brand */
.mobile-only {
  display: none;
}

.brand-logo {
  height: 32px;
  width: auto;
}

/* Page Info */
.page-info {
  display: flex;
  align-items: center;
  gap: 0;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 0;
}

.page-title {
  /* Un escalón por encima del título de las Card (text-lg): la pantalla
     manda sobre las secciones que contiene. */
  font-size: var(--escality-text-xl);
  font-weight: var(--escality-font-weight-semibold);
  color: var(--escality-gray-900);
  margin: 0;
  line-height: 1;
  font-family: var(--escality-font-heading);
}

/* Breadcrumb inline */
.breadcrumb-container {
  display: flex;
  align-items: center;
  font-size: var(--escality-text-sm);
  color: var(--escality-gray-500);
  margin-left: 8px;
}

.breadcrumb-separator {
  color: var(--escality-gray-300);
  margin: 0 6px;
  font-size: var(--escality-text-xs);
}

.breadcrumb-item {
  display: inline-flex;
  align-items: center;
}

.breadcrumb-link {
  color: var(--escality-gray-500);
  text-decoration: none;
  transition: color 0.15s;
}

.breadcrumb-link:hover {
  color: var(--escality-primary);
}

.breadcrumb-current {
  color: var(--escality-gray-900);
  font-weight: var(--escality-font-weight-medium);
}

/* Subscription Chip */
.subscription-chip {
  background: linear-gradient(135deg, var(--escality-warning-50), #fed7aa);
  color: #92400e;
  border: 1px solid #fbbf24;
  font-size: var(--escality-text-xs);
  font-weight: var(--escality-font-weight-medium);
}

.subscription-chip :deep(.p-chip-icon) {
  color: var(--escality-warning);
  font-size: var(--escality-text-base);
}

/* Billing Button States */
.billing-btn.billing-pending {
  color: var(--escality-warning) !important;
  animation: billing-pulse 2s ease-in-out infinite;
}

.billing-btn.billing-overdue {
  color: var(--escality-danger) !important;
  animation: billing-pulse 1s ease-in-out infinite;
}

@keyframes billing-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Navigation Buttons */
.nav-btn {
  width: 40px;
  height: 40px;
  border-radius: var(--escality-radius-md);
  background: transparent;
  border: none;
  color: var(--escality-gray-500);
  transition: all 0.2s;
}

.nav-btn:hover {
  background: var(--escality-gray-100) !important;
  color: var(--escality-gray-900) !important;
  transform: translateY(-1px);
}

.nav-btn:focus {
  box-shadow: none !important;
}

.nav-btn.has-notifications {
  color: var(--escality-primary);
}

/* Notification Badge */
.notifications-btn :deep(.p-badge) {
  min-width: 18px;
  height: 18px;
  font-size: 10px;
  font-weight: var(--escality-font-weight-semibold);
  background: var(--escality-danger);
  border: 2px solid white;
  top: -2px;
  right: -2px;
}

/* User Menu */
.user-menu-wrapper {
  position: relative;
}

.user-menu-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px 6px 6px;
  background: var(--escality-gray-50);
  border: 1px solid var(--escality-gray-200);
  border-radius: var(--escality-radius-lg);
  cursor: pointer;
  transition: all 0.2s;
  font-family: var(--escality-font-body);
}

.user-menu-btn:hover {
  background: white;
  border-color: var(--escality-gray-300);
  box-shadow: var(--escality-shadow-sm);
  transform: translateY(-1px);
}

.user-menu-btn.menu-open {
  background: white;
  border-color: var(--escality-primary);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.user-avatar :deep(.p-avatar) {
  background: var(--escality-gradient-primary);
  color: white;
  font-weight: var(--escality-font-weight-semibold);
  border-radius: var(--escality-radius-md);
}

.user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
}

.user-name {
  font-size: var(--escality-text-sm);
  font-weight: var(--escality-font-weight-semibold);
  color: var(--escality-gray-900);
}

.user-role {
  font-size: 11px;
  color: var(--escality-gray-500);
  font-weight: var(--escality-font-weight-medium);
}

.menu-arrow {
  font-size: var(--escality-text-xs);
  color: var(--escality-gray-400);
  transition: transform 0.2s;
  margin-left: 4px;
}

.user-menu-btn.menu-open .menu-arrow {
  transform: rotate(180deg);
}

/* Quick Actions Panel */
.quick-actions-panel {
  padding: 20px;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  color: var(--escality-gray-900);
}

.panel-header i {
  font-size: 20px;
  color: var(--escality-primary);
}

.panel-header h5 {
  margin: 0;
  font-size: var(--escality-text-md);
  font-weight: var(--escality-font-weight-semibold);
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.action-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 12px;
  background: var(--escality-gray-50);
  border: 1px solid var(--escality-gray-200);
  border-radius: var(--escality-radius-lg);
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}

.action-card:hover {
  background: white;
  border-color: var(--escality-primary);
  transform: translateY(-2px);
  box-shadow: var(--escality-shadow-md);
}

.action-icon {
  width: 48px;
  height: 48px;
  border-radius: var(--escality-radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  transition: transform 0.2s;
}

.action-card:hover .action-icon {
  transform: scale(1.05);
}

.action-card span {
  font-size: var(--escality-text-xs);
  font-weight: var(--escality-font-weight-medium);
  color: var(--escality-gray-700);
}

/* Icon backgrounds */
.icon-sales {
  background: var(--escality-success-50);
  color: var(--escality-success-dark);
}

.icon-products {
  background: var(--escality-primary-100);
  color: var(--escality-primary);
}

.icon-clients {
  background: #fce7f3;
  color: #db2777;
}

.icon-reports {
  background: var(--escality-warning-50);
  color: var(--escality-warning-dark);
}

.icon-categories {
  background: #e9d5ff;
  color: #9333ea;
}

.icon-downloads {
  background: #f3e8ff;
  color: #7c3aed;
}

/* User Menu Panel */
.user-menu-panel {
  padding: 0;
}

.user-header {
  padding: 20px;
  background: linear-gradient(135deg, var(--escality-primary-50), var(--escality-primary-100));
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
}

.user-avatar-large :deep(.p-avatar) {
  background: var(--escality-gradient-primary);
  color: white;
  font-weight: var(--escality-font-weight-semibold);
  font-size: 24px;
  border-radius: 16px;
}

.user-details h5 {
  margin: 0;
  font-size: var(--escality-text-md);
  font-weight: var(--escality-font-weight-semibold);
  color: var(--escality-gray-900);
}

.user-email {
  font-size: var(--escality-text-sm);
  color: var(--escality-gray-500);
  margin-bottom: 8px;
  display: block;
}

.user-status :deep(.p-chip) {
  background: white;
  border: 1px solid var(--escality-gray-200);
  font-size: 11px;
  padding: 4px 8px;
}

/* User Menu Items */
.user-menu-panel :deep(.p-menu) {
  border: none;
  padding: 8px;
  background: transparent;
}

.user-menu-panel :deep(.p-menu .p-submenu-header) {
  color: var(--escality-gray-500);
  font-size: 11px;
  font-weight: var(--escality-font-weight-semibold);
  text-transform: uppercase;
  padding: 8px 12px;
}

.user-menu-panel :deep(.p-menu .p-menuitem-link) {
  border-radius: var(--escality-radius-md);
  padding: 10px 12px;
  transition: all 0.2s;
}

.user-menu-panel :deep(.p-menu .p-menuitem-link:hover) {
  background: var(--escality-gray-100);
}

.user-menu-panel :deep(.logout-item .p-menuitem-link) {
  color: var(--escality-danger-dark);
}

.user-menu-panel :deep(.logout-item .p-menuitem-icon) {
  color: var(--escality-danger-dark);
}

/* Help Sidebar */
.sidebar-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--escality-text-lg);
  font-weight: var(--escality-font-weight-semibold);
  color: var(--escality-gray-900);
}

.sidebar-header i {
  color: var(--escality-primary);
}

.help-content {
  padding: 20px;
}

.help-card :deep(.p-card) {
  border: 1px solid var(--escality-gray-200);
  border-radius: var(--escality-radius-lg);
  box-shadow: var(--escality-shadow-sm);
}

.help-card :deep(.p-card-header) {
  text-align: center;
  padding: 20px;
}

.help-icon {
  font-size: 48px;
  color: var(--escality-primary);
}

.help-card :deep(.p-card-title) {
  font-size: var(--escality-text-md);
  font-weight: var(--escality-font-weight-semibold);
  color: var(--escality-gray-900);
  margin-bottom: 8px;
}

.help-card :deep(.p-card-content p) {
  color: var(--escality-gray-500);
  font-size: var(--escality-text-base);
  margin-bottom: 16px;
}

/* Transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter-from {
  transform: translateX(10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(-10px);
  opacity: 0;
}

/* Responsive */
@media (max-width: 1024px) {
  .actions-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .user-info .user-role {
    display: none;
  }
}

@media (max-width: 768px) {
  .escality-navbar {
    left: 0;
  }

  .navbar-toolbar {
    padding: 0 16px;
  }

  .mobile-only {
    display: block;
    margin-right: 12px;
  }

  /* El template usa .breadcrumb-container; apuntaba a .page-breadcrumb,
     que no existe, así que en mobile el breadcrumb nunca se ocultaba. */
  .breadcrumb-container {
    display: none;
  }

  .subscription-chip {
    display: none;
  }

  .user-info {
    display: none;
  }

  .navbar-toolbar :deep(.p-toolbar-separator) {
    display: none;
  }
}

/* Notification Badge Wrapper */
.notification-wrapper {
  position: relative;
  display: inline-block;
}

.notification-wrapper .notification-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  font-size: 10px;
}
</style>